/**
 * A sessão de suporte, do lado do navegador.
 *
 * ## Por que a tela precisa saber disto antes de tentar escrever
 *
 * Porque o servidor recusa toda escrita numa sessão de suporte
 * (`recusarEscritaDeSuporte`), e a recusa é a resposta certa — mas ela chega
 * **depois** do clique. No mural isso significava: o operador via "Escrever a
 * primeira", abria o formulário, digitava a pergunta inteira e só ao clicar em
 * "Publicar" descobria que aquela sessão não escreve. O texto era jogado fora,
 * e o cliente já sabia que estava numa sessão de suporte — a faixa está na tela
 * o tempo todo.
 *
 * O padrão que este produto já usa para "o controle existe e não pode ser usado
 * agora" é o do `MediaUpload` quando o armazenamento está fora: **controle
 * desabilitado, com o motivo escrito ao lado**. É o mesmo aqui, e de propósito o
 * mesmo — um terceiro jeito de dizer "não dá" seria uma terceira coisa a
 * aprender.
 *
 * A recusa do servidor continua existindo, e continua sendo a autoridade: esta
 * leitura é de um cookie, e cookie se apaga. Ela só decide o que a tela OFERECE.
 */

import { caminhoInternoSeguro } from '@vestigo/core/caminho-interno';
import type { Chave } from '@vestigo/core/i18n-da-tela';

/** O cookie que o servidor escreve junto com a sessão. Ver `member-sessions.ts`. */
const COOKIE = 'vestigo_aluno_suporte';

/** O que o servidor codificou no cookie. Ver `FaixaDeSuporte`, em `member-sessions.ts`. */
export interface Faixa {
  aluno: string;
  email: string;
  operador: string;
  expiraEm: string;
  voltarPara: string;
}

/**
 * A faixa da sessão de suporte, ou `null` para o aluno de verdade.
 *
 * O aluno paga por este recurso existir exatamente uma leitura de `document.cookie`.
 */
export function lerFaixaDeSuporte(): Faixa | null {
  const achado = document.cookie.match(new RegExp(`(?:^|;\\s*)${COOKIE}=([^;]+)`));
  if (!achado) return null;

  try {
    /* base64url → base64: `atob` não conhece os caracteres trocados, e um `-` no
     * meio do JSON derrubaria a faixa inteira em vez de um caractere. */
    const bruto = decodeURIComponent(achado[1]!).replace(/-/g, '+').replace(/_/g, '/');
    const texto = new TextDecoder().decode(Uint8Array.from(atob(bruto), (c) => c.charCodeAt(0)));
    const dados = JSON.parse(texto) as Faixa;
    /* Um cookie adulterado não pode virar um redirecionamento para fora: o
     * servidor já exige caminho relativo, e a tela confere de novo porque este
     * valor passou pelo navegador no meio do caminho.
     *
     * A regra é a MESMA função que o servidor usa (`caminhoInternoSeguro`), e não
     * uma segunda regex: a regex que estava aqui — e a do servidor — deixavam
     * passar `/\evil.com`, que vira `https://evil.com` no `window.location.href`
     * de `FaixaDeSuporte`. Duas cópias da mesma guarda divergem, e a que fica
     * para trás é a que vaza. */
    dados.voltarPara = caminhoInternoSeguro(dados.voltarPara) ?? '';
    return dados;
  } catch {
    return null;
  }
}

/** Só a pergunta, para quem não precisa do resto. */
export function emSessaoDeSuporte(): boolean {
  return lerFaixaDeSuporte() !== null;
}

/**
 * O motivo, escrito ao lado do controle apagado — a CHAVE dele.
 *
 * É **a mesma linha do dicionário** que o servidor devolve em
 * `recusarEscritaDeSuporte` (`plugins/auth-aluno.ts`), e de propósito a mesma:
 * a recusa continua existindo para quem chegar pela API, e a tela que se
 * antecipa a ela não pode dizer outra coisa. Duas frases para o mesmo fato
 * seriam duas explicações a conferir no dia em que uma mudasse — e enquanto
 * isto era uma frase escrita duas vezes, essa conferência não tinha oráculo
 * nenhum. Agora tem: a chave.
 *
 * ## Por que uma CHAVE, e por que o nome mudou com ela
 *
 * Porque a tela do aluno é traduzida, e o que o operador de suporte vê é a
 * área **como o aluno a vê** — incluindo a língua. E porque o nome antigo
 * (`MOTIVO_DA_SESSAO_DE_SUPORTE`) prometia uma frase: um sítio que o
 * desenhasse direto imprimiria `suporte.motivoDaSessao` na cara da pessoa, e
 * nesta obra isso já aconteceu uma vez com 23 asserções verdes a comparar
 * chave com chave. Trocando o nome, o `tsc` acende os 17 sítios de uma vez e
 * o conserto não pode ficar a meio.
 */
export const CHAVE_DO_MOTIVO_DE_SUPORTE = 'suporte.motivoDaSessao' satisfies Chave;
